<script setup>
import { reactive, computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useVuelidate } from '@vuelidate/core';
import { required, minLength } from '@vuelidate/validators';
import Draggable from 'vuedraggable';

import Input from 'dashboard/components-next/input/Input.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Select from 'dashboard/components-next/select/Select.vue';
import Switch from 'dashboard/components-next/switch/Switch.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import AssistantFieldFillingSection from './AssistantFieldFillingSection.vue';

const props = defineProps({
  assistant: {
    type: Object,
    default: () => ({}),
  },
});

const { t } = useI18n();
const ROLE_OPTIONS = [
  {
    value: 'atendimento',
    label: t('CAPTAIN.ASSISTANTS.FORM.AGENT_ROLE.OPTIONS.ATENDIMENTO.LABEL'),
    description: t(
      'CAPTAIN.ASSISTANTS.FORM.AGENT_ROLE.OPTIONS.ATENDIMENTO.DESCRIPTION'
    ),
  },
  {
    value: 'sdr',
    label: t('CAPTAIN.ASSISTANTS.FORM.AGENT_ROLE.OPTIONS.SDR.LABEL'),
    description: t(
      'CAPTAIN.ASSISTANTS.FORM.AGENT_ROLE.OPTIONS.SDR.DESCRIPTION'
    ),
  },
  {
    value: 'vendedor',
    label: t('CAPTAIN.ASSISTANTS.FORM.AGENT_ROLE.OPTIONS.VENDEDOR.LABEL'),
    description: t(
      'CAPTAIN.ASSISTANTS.FORM.AGENT_ROLE.OPTIONS.VENDEDOR.DESCRIPTION'
    ),
  },
];

// Nomes nativos, alinhados aos idiomas ativos em config/initializers/languages.rb
// (LANGUAGES_CONFIG) — mantém uma única fonte curada de idiomas no frontend,
// sem depender de window.chatwootConfig (limitado hoje a en/pt_BR).
const LANGUAGE_OPTIONS = [
  { value: 'pt-BR', label: 'Português Brasileiro (pt-BR)' },
  { value: 'en', label: 'English (en)' },
  { value: 'es', label: 'Español (es)' },
  { value: 'pt', label: 'Português (pt)' },
  { value: 'fr', label: 'Français (fr)' },
  { value: 'de', label: 'Deutsch (de)' },
  { value: 'it', label: 'Italiano (it)' },
  { value: 'nl', label: 'Nederlands (nl)' },
  { value: 'ar', label: 'العربية (ar)' },
  { value: 'ja', label: '日本語 (ja)' },
  { value: 'ko', label: '한국어 (ko)' },
  { value: 'ru', label: 'русский (ru)' },
  { value: 'zh_CN', label: '中文 (zh-CN)' },
  { value: 'zh_TW', label: '中文 (台湾) (zh-TW)' },
  { value: 'vi', label: 'Tiếng Việt (vi)' },
  { value: 'tr', label: 'Türkçe (tr)' },
  { value: 'pl', label: 'język polski (pl)' },
  { value: 'sv', label: 'Svenska (sv)' },
  { value: 'da', label: 'dansk (da)' },
  { value: 'fi', label: 'suomi, suomen kieli (fi)' },
  { value: 'id', label: 'Bahasa Indonesia (id)' },
  { value: 'th', label: 'ภาษาไทย (th)' },
  { value: 'he', label: 'עִברִית (he)' },
  { value: 'el', label: 'ελληνικά (el)' },
  { value: 'cs', label: 'čeština (cs)' },
  { value: 'ro', label: 'Română (ro)' },
  { value: 'hu', label: 'magyar nyelv (hu)' },
  { value: 'uk', label: 'украї́нська мо́ва (uk)' },
];

// Same order the backend permits in business_hours_params.
const WEEKDAYS = [
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday',
  'sunday',
];

const buildBusinessHours = (config = {}) => {
  const stored = config.business_hours || {};
  return WEEKDAYS.reduce((acc, day) => {
    const value = stored[day] || {};
    acc[day] = {
      enabled: value.enabled || false,
      openTime: value.open_time || '08:00',
      closeTime: value.close_time || '18:00',
    };
    return acc;
  }, {});
};

const isAlwaysOn = businessHours =>
  WEEKDAYS.every(day => businessHours[day].enabled);

const HISTORY_MESSAGE_COUNT_MIN = 5;
const HISTORY_MESSAGE_COUNT_MAX = 100;
const HISTORY_MESSAGE_COUNT_DEFAULT = 40;

const state = reactive({
  name: '',
  agentTitle: '',
  agentRole: 'atendimento',
  tone: '',
  script: '',
  defaultLanguage: 'pt-BR',
  lockToDefaultLanguage: false,
  waitTimeSeconds: 10,
  maxMessageLength: 450,
  historyMessageCount: HISTORY_MESSAGE_COUNT_DEFAULT,
  scheduleMode: 'always_on',
  businessHours: buildBusinessHours(),
  criticalRules: [],
  features: {
    conversationFaqs: false,
    memories: false,
    citations: false,
  },
  contactAttributesAllowlist: [],
  companyAttributesAllowlist: [],
  dealAttributesAllowlist: [],
});

// Stable per-item id for the draggable list's item-key — using the editable
// text itself as the key would recreate the <input> DOM node (and drop
// focus) on every keystroke.
let nextCriticalRuleId = 0;
const generateCriticalRuleId = () => {
  nextCriticalRuleId += 1;
  return nextCriticalRuleId;
};

const addCriticalRule = () => {
  state.criticalRules.push({
    id: generateCriticalRuleId(),
    text: '',
    enabled: true,
  });
};

const removeCriticalRule = index => {
  state.criticalRules.splice(index, 1);
};

const validationRules = {
  name: { required, minLength: minLength(1) },
  historyMessageCount: {
    minValue: value => Number(value) >= HISTORY_MESSAGE_COUNT_MIN,
    maxValue: value => Number(value) <= HISTORY_MESSAGE_COUNT_MAX,
  },
};

const v$ = useVuelidate(validationRules, state);

const getErrorMessage = field =>
  v$.value[field].$error ? v$.value[field].$errors[0].$message : '';

const formErrors = computed(() => ({
  name: getErrorMessage('name'),
  historyMessageCount: v$.value.historyMessageCount.$error
    ? t('CAPTAIN.ASSISTANTS.FORM.HISTORY_MESSAGE_COUNT.ERROR', {
        min: HISTORY_MESSAGE_COUNT_MIN,
        max: HISTORY_MESSAGE_COUNT_MAX,
      })
    : '',
}));

const serializeBusinessHours = () => {
  if (state.scheduleMode === 'always_on') {
    return WEEKDAYS.reduce((acc, day) => {
      acc[day] = { enabled: true, open_time: '00:00', close_time: '23:59' };
      return acc;
    }, {});
  }

  return WEEKDAYS.reduce((acc, day) => {
    const value = state.businessHours[day];
    acc[day] = {
      enabled: value.enabled,
      open_time: value.enabled ? value.openTime : '',
      close_time: value.enabled ? value.closeTime : '',
    };
    return acc;
  }, {});
};

const validate = async () => {
  const validations = [
    v$.value.name.$validate(),
    v$.value.historyMessageCount.$validate(),
  ];

  return Promise.all(validations).then(results => results.every(Boolean));
};

// O preenchimento so roda quando ha ao menos um campo marcado em alguma
// entidade; nao existe mais um interruptor separado.
const hasFieldsToFill = computed(
  () =>
    state.contactAttributesAllowlist.length > 0 ||
    state.companyAttributesAllowlist.length > 0 ||
    state.dealAttributesAllowlist.length > 0
);

// Contas antigas (sem attribute_filling_mode) com o preenchimento ligado e uma
// lista vazia entendiam "vazio = todos os campos". A secao marca tudo para
// refletir esse comportamento, sem mudar o que a IA ja fazia.
const legacyAll = reactive({ contact: false, company: false, deal: false });

const setLegacyAll = (config = {}) => {
  const legacy =
    !!config.feature_contact_attributes &&
    config.attribute_filling_mode !== 'selected';
  legacyAll.contact = legacy && !config.contact_attributes_allowlist?.length;
  legacyAll.company = legacy && !config.company_attributes_allowlist?.length;
  legacyAll.deal = legacy && !config.deal_attributes_allowlist?.length;
};

// Returns this section's partial payload only — never spreads
// props.assistant.config, so the parent page can merge all sections'
// payloads together in one PATCH instead of each section overwriting the
// others' unsaved changes with a stale snapshot of assistant.config.
const getPayload = () => ({
  name: state.name,
  config: {
    agent_title: state.agentTitle,
    agent_role: state.agentRole,
    tone: state.tone,
    script: state.script,
    default_language: state.defaultLanguage,
    detect_language: !state.lockToDefaultLanguage,
    wait_time_seconds: Number(state.waitTimeSeconds),
    max_message_length: Number(state.maxMessageLength),
    history_message_count: Number(state.historyMessageCount),
    business_hours: serializeBusinessHours(),
    critical_rules: state.criticalRules.map(rule => ({
      text: rule.text,
      enabled: rule.enabled,
    })),
    feature_faq: state.features.conversationFaqs,
    feature_memory: state.features.memories,
    feature_citation: state.features.citations,
    feature_contact_attributes: hasFieldsToFill.value,
    attribute_filling_mode: 'selected',
    contact_attributes_allowlist: state.contactAttributesAllowlist.map(
      entry => ({
        attribute_key: entry.attributeKey,
        instruction: entry.instruction,
      })
    ),
    company_attributes_allowlist: state.companyAttributesAllowlist.map(
      entry => ({
        attribute_key: entry.attributeKey,
        instruction: entry.instruction,
      })
    ),
    deal_attributes_allowlist: state.dealAttributesAllowlist.map(entry => ({
      attribute_key: entry.attributeKey,
      instruction: entry.instruction,
    })),
  },
});

// Compares serialized payloads (not raw state) so the draggable list's
// generated `id` field — which changes every time updateStateFromAssistant
// runs but is never part of what gets saved — doesn't cause a false
// positive dirty state.
let initialPayloadSnapshot = JSON.stringify(getPayload());

const isDirty = computed(
  () => JSON.stringify(getPayload()) !== initialPayloadSnapshot
);

const onLegacyResolved = keys => {
  keys.forEach(key => {
    legacyAll[key] = false;
  });
  initialPayloadSnapshot = JSON.stringify(getPayload());
};

const updateStateFromAssistant = assistant => {
  const { config = {} } = assistant;
  state.name = assistant.name;
  state.agentTitle = config.agent_title || '';
  state.agentRole = config.agent_role || 'atendimento';
  state.tone = config.tone || '';
  state.script = config.script || '';
  state.defaultLanguage = config.default_language || 'pt-BR';
  state.lockToDefaultLanguage = config.detect_language === false;
  state.waitTimeSeconds = config.wait_time_seconds || 10;
  state.maxMessageLength = config.max_message_length || 450;
  state.historyMessageCount =
    config.history_message_count || HISTORY_MESSAGE_COUNT_DEFAULT;
  state.businessHours = buildBusinessHours(config);
  state.scheduleMode = isAlwaysOn(state.businessHours) ? 'always_on' : 'custom';
  state.criticalRules = (config.critical_rules || []).map(rule => ({
    id: generateCriticalRuleId(),
    text: rule.text || '',
    enabled: rule.enabled !== false,
  }));
  state.features = {
    conversationFaqs: config.feature_faq || false,
    memories: config.feature_memory || false,
    citations: config.feature_citation || false,
  };
  state.contactAttributesAllowlist = (
    config.contact_attributes_allowlist || []
  ).map(entry => ({
    attributeKey: entry.attribute_key,
    instruction: entry.instruction || '',
  }));
  state.companyAttributesAllowlist = (
    config.company_attributes_allowlist || []
  ).map(entry => ({
    attributeKey: entry.attribute_key,
    instruction: entry.instruction || '',
  }));
  state.dealAttributesAllowlist = (config.deal_attributes_allowlist || []).map(
    entry => ({
      attributeKey: entry.attribute_key,
      instruction: entry.instruction || '',
    })
  );
  setLegacyAll(config);
  initialPayloadSnapshot = JSON.stringify(getPayload());
};

defineExpose({ validate, getPayload, isDirty });

watch(
  () => props.assistant,
  newAssistant => {
    if (newAssistant) updateStateFromAssistant(newAssistant);
  },
  { immediate: true }
);
</script>

<template>
  <div class="flex flex-col gap-6">
    <Input
      v-model="state.name"
      :label="t('CAPTAIN.ASSISTANTS.FORM.NAME.LABEL')"
      :placeholder="t('CAPTAIN.ASSISTANTS.FORM.NAME.PLACEHOLDER')"
      :message="formErrors.name"
      :message-type="formErrors.name ? 'error' : 'info'"
    />

    <Input
      v-model="state.agentTitle"
      :label="t('CAPTAIN.ASSISTANTS.FORM.AGENT_TITLE.LABEL')"
      :placeholder="t('CAPTAIN.ASSISTANTS.FORM.AGENT_TITLE.PLACEHOLDER')"
    />

    <div class="flex flex-col gap-2">
      <label class="text-sm font-medium text-n-slate-12">
        {{ t('CAPTAIN.ASSISTANTS.FORM.AGENT_ROLE.LABEL') }}
      </label>
      <div class="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <button
          v-for="option in ROLE_OPTIONS"
          :key="option.value"
          type="button"
          class="flex flex-col items-start gap-1 p-3 text-left border rounded-lg transition-colors duration-200"
          :class="
            state.agentRole === option.value
              ? 'border-n-brand bg-n-alpha-1'
              : 'border-n-weak hover:border-n-slate-6 bg-n-alpha-black2'
          "
          @click="state.agentRole = option.value"
        >
          <span class="text-sm font-medium text-n-slate-12">
            {{ option.label }}
          </span>
          <span class="text-xs text-n-slate-11">
            {{ option.description }}
          </span>
        </button>
      </div>
    </div>

    <Input
      v-model="state.tone"
      :label="t('CAPTAIN.ASSISTANTS.FORM.TONE.LABEL')"
      :placeholder="t('CAPTAIN.ASSISTANTS.FORM.TONE.PLACEHOLDER')"
    />

    <TextArea
      v-model="state.script"
      :label="t('CAPTAIN.ASSISTANTS.FORM.SCRIPT.LABEL')"
      :placeholder="t('CAPTAIN.ASSISTANTS.FORM.SCRIPT.PLACEHOLDER')"
      :max-length="4000"
      show-character-count
      auto-height
      resize
    />

    <div class="flex flex-col gap-2">
      <label class="text-sm font-medium text-n-slate-12">
        {{ t('CAPTAIN.ASSISTANTS.FORM.DEFAULT_LANGUAGE.LABEL') }}
      </label>
      <Select v-model="state.defaultLanguage" :options="LANGUAGE_OPTIONS" />
    </div>

    <div class="flex items-center gap-3">
      <Switch v-model="state.lockToDefaultLanguage" />
      <span class="text-sm text-n-slate-12">
        {{ t('CAPTAIN.ASSISTANTS.FORM.LOCK_TO_DEFAULT_LANGUAGE.LABEL') }}
      </span>
    </div>

    <Input
      v-model="state.waitTimeSeconds"
      type="number"
      min="1"
      :label="t('CAPTAIN.ASSISTANTS.FORM.WAIT_TIME_SECONDS.LABEL')"
      :message="t('CAPTAIN.ASSISTANTS.FORM.WAIT_TIME_SECONDS.HELP')"
      message-type="info"
    />

    <div class="flex flex-col gap-2">
      <label class="text-sm font-medium text-n-slate-12">
        {{ t('CAPTAIN.ASSISTANTS.FORM.HISTORY_MESSAGE_COUNT.LABEL') }}
      </label>
      <span class="text-xs text-n-slate-11">
        {{ t('CAPTAIN.ASSISTANTS.FORM.HISTORY_MESSAGE_COUNT.DESCRIPTION') }}
      </span>
      <Input
        v-model="state.historyMessageCount"
        type="number"
        :min="String(HISTORY_MESSAGE_COUNT_MIN)"
        :max="String(HISTORY_MESSAGE_COUNT_MAX)"
        :message="formErrors.historyMessageCount"
        :message-type="formErrors.historyMessageCount ? 'error' : 'info'"
      />
      <input
        v-model="state.historyMessageCount"
        type="range"
        :min="HISTORY_MESSAGE_COUNT_MIN"
        :max="HISTORY_MESSAGE_COUNT_MAX"
        class="w-full accent-n-brand"
      />
      <span class="text-xs text-n-slate-11">
        {{
          t('CAPTAIN.ASSISTANTS.FORM.HISTORY_MESSAGE_COUNT.HELP', {
            min: HISTORY_MESSAGE_COUNT_MIN,
            max: HISTORY_MESSAGE_COUNT_MAX,
          })
        }}
      </span>
    </div>

    <Input
      v-model="state.maxMessageLength"
      type="number"
      min="100"
      :label="t('CAPTAIN.ASSISTANTS.FORM.MAX_MESSAGE_LENGTH.LABEL')"
      :message="t('CAPTAIN.ASSISTANTS.FORM.MAX_MESSAGE_LENGTH.HELP')"
      message-type="info"
    />

    <div class="flex flex-col gap-3">
      <label class="text-sm font-medium text-n-slate-12">
        {{ t('CAPTAIN.ASSISTANTS.FORM.SCHEDULE.LABEL') }}
      </label>
      <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <button
          type="button"
          class="flex flex-col items-start gap-1 p-3 text-left border rounded-lg transition-colors duration-200"
          :class="
            state.scheduleMode === 'always_on'
              ? 'border-n-brand bg-n-alpha-1'
              : 'border-n-weak hover:border-n-slate-6 bg-n-alpha-black2'
          "
          @click="state.scheduleMode = 'always_on'"
        >
          <span class="text-sm font-medium text-n-slate-12">
            {{ t('CAPTAIN.ASSISTANTS.FORM.SCHEDULE.ALWAYS_ON.LABEL') }}
          </span>
          <span class="text-xs text-n-slate-11">
            {{ t('CAPTAIN.ASSISTANTS.FORM.SCHEDULE.ALWAYS_ON.DESCRIPTION') }}
          </span>
        </button>
        <button
          type="button"
          class="flex flex-col items-start gap-1 p-3 text-left border rounded-lg transition-colors duration-200"
          :class="
            state.scheduleMode === 'custom'
              ? 'border-n-brand bg-n-alpha-1'
              : 'border-n-weak hover:border-n-slate-6 bg-n-alpha-black2'
          "
          @click="state.scheduleMode = 'custom'"
        >
          <span class="text-sm font-medium text-n-slate-12">
            {{ t('CAPTAIN.ASSISTANTS.FORM.SCHEDULE.CUSTOM.LABEL') }}
          </span>
          <span class="text-xs text-n-slate-11">
            {{ t('CAPTAIN.ASSISTANTS.FORM.SCHEDULE.CUSTOM.DESCRIPTION') }}
          </span>
        </button>
      </div>

      <div v-if="state.scheduleMode === 'custom'" class="flex flex-col gap-2">
        <div v-for="day in WEEKDAYS" :key="day" class="flex items-center gap-3">
          <div class="flex items-center gap-2 w-40">
            <Switch v-model="state.businessHours[day].enabled" />
            <span class="text-sm text-n-slate-12">
              {{ t(`CAPTAIN.ASSISTANTS.FORM.BUSINESS_HOURS.DAYS.${day}`) }}
            </span>
          </div>
          <template v-if="state.businessHours[day].enabled">
            <input
              v-model="state.businessHours[day].openTime"
              type="time"
              class="bg-n-alpha-black2 rounded-lg px-2 py-1 text-sm text-n-slate-12"
            />
            <span class="text-n-slate-11">{{
              t('CAPTAIN.ASSISTANTS.FORM.BUSINESS_HOURS.TO')
            }}</span>
            <input
              v-model="state.businessHours[day].closeTime"
              type="time"
              class="bg-n-alpha-black2 rounded-lg px-2 py-1 text-sm text-n-slate-12"
            />
          </template>
          <span v-else class="text-sm text-n-slate-11">
            {{ t('CAPTAIN.ASSISTANTS.FORM.BUSINESS_HOURS.CLOSED') }}
          </span>
        </div>
      </div>
    </div>

    <div class="flex flex-col gap-3">
      <label class="text-sm font-medium text-n-slate-12">
        {{ t('CAPTAIN.ASSISTANTS.FORM.CRITICAL_RULES.LABEL') }}
      </label>
      <span class="text-xs text-n-slate-11">
        {{ t('CAPTAIN.ASSISTANTS.FORM.CRITICAL_RULES.DESCRIPTION') }}
      </span>
      <Draggable
        v-model="state.criticalRules"
        tag="div"
        item-key="id"
        class="flex flex-col gap-2"
        handle=".drag-handle"
      >
        <template #item="{ element: rule, index }">
          <div class="flex items-center gap-2">
            <Icon
              icon="i-lucide-grip-vertical"
              class="drag-handle size-4 text-n-slate-11 cursor-move flex-shrink-0"
            />
            <Switch v-model="rule.enabled" />
            <Input v-model="rule.text" class="flex-1" />
            <Button
              icon="i-lucide-x"
              variant="ghost"
              color="slate"
              size="sm"
              @click="removeCriticalRule(index)"
            />
          </div>
        </template>
      </Draggable>
      <Button
        icon="i-lucide-plus"
        variant="ghost"
        size="sm"
        class="!w-fit !text-n-brand hover:!text-n-brand"
        :label="t('CAPTAIN.ASSISTANTS.FORM.CRITICAL_RULES.ADD')"
        @click="addCriticalRule"
      />
    </div>

    <AssistantFieldFillingSection
      v-model:contact="state.contactAttributesAllowlist"
      v-model:company="state.companyAttributesAllowlist"
      v-model:deal="state.dealAttributesAllowlist"
      :legacy-all="legacyAll"
      @legacy-resolved="onLegacyResolved"
    />
  </div>
</template>
